// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * User Service
 * API calls for user management (org resolved from JWT by the BFF)
 */

import { get, post, put, del } from './api';
import { ENDPOINTS } from '../utils/constants';
import type { UserResponse, UserInvite, UserUpdate } from '../types';
import type { MeResponse } from '../types';

interface GetUsersParams {
  skip?: number;
  limit?: number;
}

/**
 * Get all users (org resolved from JWT by the BFF)
 */
export const getUsers = async (params: GetUsersParams = {}): Promise<UserResponse[]> => {
  const queryParams = new URLSearchParams();
  if (params.skip !== undefined) queryParams.append('skip', String(params.skip));
  if (params.limit !== undefined) queryParams.append('limit', String(params.limit));

  const url = queryParams.toString() ? `${ENDPOINTS.USERS}?${queryParams}` : ENDPOINTS.USERS;
  return get<UserResponse[]>(url);
};

/**
 * The BFF's code on a /me 401 when IAM accepted the token but has no user
 * for it, e.g. a deleted or re-invited user's old token (AB#35972).
 */
export const USER_NOT_RESOLVED = 'USER_NOT_RESOLVED';

/**
 * Get current authenticated user's profile via /api/v1/me
 * (identity resolved from JWT by the BFF, no user ID needed).
 *
 * A USER_NOT_RESOLVED 401 doesn't lock the session (re-auth would get the
 * same token back): AuthContext clears the cached profile and signs the user
 * out. Any other 401 is a rejected token and locks the session, as on every
 * other route.
 */
export const getCurrentUser = async (options: RequestInit = {}): Promise<MeResponse> => {
  return get<MeResponse>(ENDPOINTS.ME, {
    ...options,
    endSessionOn401: code => code !== USER_NOT_RESOLVED,
  });
};

/**
 * Get user by ID
 */
export const getUserById = async (userId: string): Promise<UserResponse> => {
  return get<UserResponse>(ENDPOINTS.USER_BY_ID(userId));
};

/**
 * Invite a new user (org resolved from JWT by the BFF)
 */
export const inviteUser = async (userData: UserInvite): Promise<UserResponse> => {
  return post<UserResponse>(ENDPOINTS.INVITE_USER, userData);
};

/**
 * Update user details
 */
export const updateUser = async (userId: string, userData: UserUpdate): Promise<UserResponse> => {
  return put<UserResponse>(ENDPOINTS.USER_BY_ID(userId), userData);
};

/**
 * Delete a user
 */
export const deleteUser = async (userId: string): Promise<null> => {
  return del<null>(ENDPOINTS.USER_BY_ID(userId));
};
